import $ from 'jquery';

const defaults = {
  className: 'autosizejs',
  id: 'autosizejs',
  append: '\n',
  callback: false,
  resizeDelay: 10,
  placeholder: true,
};

// line-height is conditionally included because IE7/IE8/old Opera do not return the correct value.
const typographyStyles = [
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'letterSpacing',
  'textTransform',
  'wordSpacing',
  'textIndent',
  'whiteSpace',
];

// to keep track which textarea is being mirrored when adjust() is called.
let mirrored;

// the mirror element, which is used to calculate what size the mirrored element should be.
let mirror;

function initMirror() {
  if (mirror) {
    return;
  }

  mirror = $('<textarea tabindex="-1"/>').data('autosize', true)[0];

  // 'border: 0' is unnecessary, but avoids a bug in Firefox on OSX
  mirror.style.cssText =
    '' +
    'position: absolute;' +
    'top: -999px;' +
    'left: 0;' +
    'right: auto;' +
    'bottom: auto;' +
    'border: 0;' +
    'padding: 0;' +
    'box-sizing: content-box;' +
    'word-wrap: break-word;' +
    'height: 0 !important;' +
    'min-height: 0 !important;' +
    'overflow: hidden;' +
    'transition: none;' +
    '-webkit-transition: none;';

  // test that line-height can be accurately copied.
  mirror.style.lineHeight = '99px';
  if ($(mirror).css('lineHeight') === '99px') {
    typographyStyles.push('lineHeight');
  }
  mirror.style.lineHeight = '';
}

export default function autosize($textarea, options) {
  if (!$textarea.length) {
    return;
  }

  initMirror();

  const allOptions = $.extend({}, defaults, options || {});

  if (mirror.parentNode !== document.body) {
    $(document.body).append(mirror);
  }

  $textarea.each((i, ta) => {
    const $ta = $(ta);
    const originalStyles = {
      height: ta.style.height,
      overflow: ta.style.overflow,
      overflowY: ta.style.overflowY,
      wordWrap: ta.style.wordWrap,
      resize: ta.style.resize,
    };
    const taResize = $ta.css('resize');
    let maxHeight;
    let boxOffset = 0;
    let timeout;
    let taWidth = $ta.width();

    if ($ta.data('autosize')) {
      // exit if autosize has already been applied, or if the textarea is the mirror element.
      return;
    }
    $ta.data('autosize', true);

    if ($ta.css('box-sizing') === 'border-box') {
      boxOffset = $ta.outerHeight() - $ta.height();
    }

    // IE8 and lower return 'auto', which parses to NaN, if no min-height is set.
    const minHeight = Math.max(parseFloat($ta.css('minHeight')) - boxOffset || 0, $ta.height());

    $ta.css({
      overflow: 'hidden',
      overflowY: 'hidden',
      // horizontal overflow is hidden, so break-word is necessary for handling words longer
      // than the textarea width
      wordWrap: 'break-word',
    });

    if (taResize === 'vertical') {
      $ta.css('resize', 'none');
    } else if (taResize === 'both') {
      $ta.css('resize', 'horizontal');
    }

    // getComputedStyle is preferred here because it preserves sub-pixel values, while jQuery's
    // .width() rounds to an integer
    function setWidth() {
      let width;
      const style = window.getComputedStyle ? window.getComputedStyle(ta, null) : null;

      if (style) {
        width = parseFloat(style.width);
        if (
          style.boxSizing === 'border-box' ||
          style.webkitBoxSizing === 'border-box' ||
          style.mozBoxSizing === 'border-box'
        ) {
          ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth'].forEach(val => {
            width -= parseFloat(style[val]);
          });
        }
      } else {
        width = $ta.width();
      }

      mirror.style.width = `${Math.max(width, 0)}px`;
    }

    function initMirror() {
      const styles = {};

      mirrored = ta;
      mirror.className = allOptions.className;
      mirror.id = allOptions.id;
      maxHeight = parseFloat($ta.css('maxHeight'));

      // mirror is a duplicate textarea located off-screen that
      // is automatically updated to contain the same text as the
      // original textarea.  mirror always has a height of 0.
      // This gives a cross-browser supported way getting the actual
      // height of the text, through the scrollTop property.
      typographyStyles.forEach(val => {
        styles[val] = $ta.css(val);
      });

      $(mirror).css(styles).attr('wrap', $ta.attr('wrap'));

      setWidth();

      // Chrome-specific fix:
      // when the textarea y-overflow is hidden, Chrome doesn't reflow the text to account
      // for the space made available by removing the scrollbar. This workaround triggers
      // the reflow for Chrome
      if (window.chrome) {
        const { width } = ta.style;
        ta.style.width = '0px';
        // eslint-disable-next-line no-unused-vars
        const ignore = ta.offsetWidth;
        ta.style.width = width;
      }
    }

    // Using mainly bare JS in this function because it is going
    // to fire very often while typing, and needs to very efficient.
    function adjust() {
      let height;

      if (mirrored !== ta) {
        initMirror();
      } else {
        setWidth();
      }

      if (!ta.value && allOptions.placeholder) {
        // If the textarea is empty, copy the placeholder text into
        // the mirror control and use that for sizing so that we
        // don't end up with placeholder getting trimmed.
        mirror.value = $ta.attr('placeholder') || '';
      } else {
        mirror.value = ta.value;
      }

      mirror.value += allOptions.append || '';
      mirror.style.overflowY = ta.style.overflowY;
      const originalHeight = parseFloat(ta.style.height) || 0;

      // setting scrollTop to zero is needed in IE8 and lower for the next step
      // to be accurately applied
      mirror.scrollTop = 0;

      mirror.scrollTop = 9e4;

      // using scrollTop rather than scrollHeight because scrollHeight is non-standard
      // and includes padding
      height = mirror.scrollTop;

      if (maxHeight && height > maxHeight) {
        ta.style.overflowY = 'scroll';
        height = maxHeight;
      } else {
        ta.style.overflowY = 'hidden';
        if (height < minHeight) {
          height = minHeight;
        }
      }

      height += boxOffset;

      if (Math.abs(originalHeight - height) > 1 / 100) {
        ta.style.height = `${height}px`;

        // trigger a repaint for IE8 for when ta is nested 2 or more levels
        // inside an inline-block
        mirror.className = mirror.className;

        if (typeof allOptions.callback === 'function') {
          allOptions.callback.call(ta, ta);
        }
        $ta.trigger('autosize.resized');
      }
    }

    const resize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        const newWidth = $ta.width();

        if (newWidth !== taWidth) {
          taWidth = newWidth;
          adjust();
        }
      }, parseInt(allOptions.resizeDelay, 10));
    };

    if ('onpropertychange' in ta) {
      if ('oninput' in ta) {
        // Detects IE9.  IE9 does not fire onpropertychange or oninput for deletions,
        // so binding to onkeyup to catch most of those occasions.  There is no way that I
        // know of to detect something like 'cut' in IE9.
        $ta.on('input.autosize keyup.autosize', adjust);
      } else {
        // IE7 / IE8
        $ta.on('propertychange.autosize', e => {
          if (e.propertyName === 'value') {
            adjust();
          }
        });
      }
    } else {
      // Modern Browsers
      $ta.on('input.autosize', adjust);
    }

    // Set allOptions.resizeDelay to false if using fixed-width textarea elements.
    // Uses a timeout and width check to reduce the amount of times adjust needs
    // to be called after window resize.

    if (allOptions.resizeDelay !== false) {
      $(window).on('resize.autosize', resize);
    }

    // Event for manual triggering if needed.
    // Should only be needed when the value of the textarea is changed through JavaScript
    // rather than user input.
    $ta.on('autosize.resize', adjust);

    // Event for manual triggering that also forces the styles to update as well.
    // Should only be needed if one of typography styles of the textarea change, and
    // the textarea is already the target of the adjust method.
    $ta.on('autosize.resizeIncludeStyle', () => {
      mirrored = null;
      adjust();
    });

    $ta.on('autosize.destroy', () => {
      mirrored = null;
      clearTimeout(timeout);
      $(window).off('resize', resize);
      $ta.off('autosize').off('.autosize').css(originalStyles).removeData('autosize');
    });

    // Call adjust in case the textarea already contains text.
    adjust();
  });
}
